/**
 * What happens when the GRADING SCHEME step finishes — all of it, in one file.
 *
 * THREE PRODUCERS SHARE THIS: `gsExtraction`, `gsGeneration` (per root) and `gsRefine` (which covers both the
 * content and the grammar engine, branching on `payload.target`). Same reason as `master-solution.step.ts`: three
 * types cannot each own the same declaration, and all three must point at THIS object — checked at boot by
 * `validateStepCompletionWiring`.
 *
 * ONE HOP, NO REFINE. The scheme itself changed, so grading reads something new for these tasks — this is the end
 * of the chain rather than a link in it.
 *
 * IT NO LONGER HARVESTS INTENTS. This used to claim the `regrade` intent off every refine row of the run and
 * dispatch each one, which needed a `harvested` flag per row to keep a closure that fires twice from
 * double-dispatching. A refine now dispatches its own (`dispatchOwnRegrade` in `gs-refine.job.ts`), because the row
 * that carries the scope is reached exactly once when it completes and needs nothing claimed. What is left here is
 * the case with no requester: a scheme that was UPLOADED or GENERATED, where the only thing anyone can say about
 * the re-grade is which tasks changed.
 */

import { logger } from '@adornis/base/logging.js';
import { mainTaskNumbersForIds, producerOf, stepIsCompleted, type FinishedRun, type StepCompletion } from '../step.js';

async function regradeWhatTheSchemeChanged(run: FinishedRun): Promise<boolean> {
  const { examId, examState, changedTasks } = run;

  // Nothing changed, so nothing downstream needs redoing — see the same guard in `master-solution.step.ts`.
  if (changedTasks.length === 0) return false;
  if (!examState?.hasGradingStarted) return false;

  const taskRefs = await mainTaskNumbersForIds(examId, changedTasks);
  if (taskRefs.length === 0) return false;

  const { dispatchRegradeIfGrading, queuedRegradeRefs } = await import('../../pipeline/regrade.js');

  // TASKS A REFINE ALREADY SPOKE FOR ARE LEFT ALONE, and this is the one guard that has to be here rather than in
  // the dispatcher. A refine's re-grade is SCOPED — to one family (`target`), and sometimes to a subset of
  // submissions: a manual grading insight re-grades every submission BUT the one the teacher just corrected by
  // hand. The dispatch below can say neither of those things, so adding it for the same task would run a second,
  // wider pass that re-grades the excluded submission and overwrites the correction that started the whole chain.
  //
  // Per ref rather than all-or-nothing: a run holding a refine of task 3 and a generated scheme for task 5 needs
  // the refine's scope for 3 and this generic dispatch for 5.
  const spokenFor = new Set(await queuedRegradeRefs(examId));
  const uncovered = taskRefs.filter(ref => !spokenFor.has(ref));
  if (uncovered.length === 0) {
    // TRUE, not false: something IS re-grading these tasks and it announces when it lands, so announcing the step
    // here as well would tell the teacher about one upload twice.
    logger.info({ examId, taskRefs }, 'Grading scheme changed — a refine already queued the re-grade');
    return true;
  }

  return dispatchRegradeIfGrading({ examId, causes: ['grading_scheme_update'], taskRefs: uncovered });
}

export const gradingSchemeStep: StepCompletion = {
  progress: {
    message: 'progress.gradingSchemeReady',
    errorMessage: 'progress.errorExtractingGradingScheme',

    // A GENERATION waits on both the task tree and the master solution it writes against; an extraction and a
    // refine wait on neither. Gating an extraction on the master solution would hold a finished upload open behind
    // one that errored, and a refine's tasks exist by definition — it is correcting them.
    precondition: (examState, record) =>
      producerOf(record) !== 'generation' ||
      (stepIsCompleted(examState, 'task') && stepIsCompleted(examState, 'masterSolution')),

    /**
     * A REFINE ANNOUNCES NOTHING, and null is how a step says so — `updateSetupStep` creates no task when every field
     * of the delta is absent. A refine was asked for in chat and answered in the same turn, so the setup-step task
     * would be the teacher being told a second time, in the words of a delivery: "das Bewertungsschema wurde
     * vollständig generiert" for a correction to one task. The follow-up still runs, so the re-grade a refine sets off
     * is unaffected.
     */
    delta: record => {
      const producer = producerOf(record);
      if (producer === 'refine') return null;
      return {
        gradingScheme: {
          type: producer,
          reupload: record.reupload === true,
          pointAdjustments: Array.isArray(record.pointAdjustments) ? record.pointAdjustments : [],
        },
      };
    },
  },

  followUp: regradeWhatTheSchemeChanged,
};
